import React, { useState } from 'react';
import { useTheme } from '../contexts/ThemeContext';
import { useAuth } from '../contexts/AuthContext';
import html2canvas from 'html2canvas';
import { storage, functions } from '../services/firebase';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { httpsCallable } from 'firebase/functions';
import Modal from './Modal';
import Toast from './Toast';

const ReportIcon: React.FC<{ color: string }> = ({ color }) => (
    <svg width="30" height="30" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ display: 'block' }}>
        <path d="M12 2C6.48 2 2 6.48 2 12C2 17.52 6.48 22 12 22C17.52 22 22 17.52 22 12C22 6.48 17.52 2 12 2ZM13 17H11V15H13V17ZM13 13H11V7H13V13Z" fill={color} />
    </svg>
);

const ScreenshotButton: React.FC = () => {
    const { theme } = useTheme();
    const { user } = useAuth();
    const [isLoading, setIsLoading] = useState(false);
    const [showModal, setShowModal] = useState(false);
    const [context, setContext] = useState('');
    const [screenshotUrl, setScreenshotUrl] = useState<string | null>(null);
    const [showToast, setShowToast] = useState(false);

    const buttonStyle = {
        color: theme.colors.WHITE,
        textDecoration: 'none',
        padding: theme.spacing.SMALL,
        margin: `0 ${theme.spacing.SMALL}px`,
        borderRadius: '4px',
        transition: 'background-color 0.2s',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        verticalAlign: 'middle',
        cursor: 'pointer',
        backgroundColor: 'transparent',
        border: 'none',
        width: '48px',
        height: '48px',
        minWidth: '48px',
        flex: '0 0 auto'
    };

    const handleScreenshot = async () => {
        try {
            const canvas = await html2canvas(document.body);
            const screenshotUrl = canvas.toDataURL('image/png');
            setScreenshotUrl(screenshotUrl);
            setShowModal(true);
        } catch (error) {
            console.error('Error taking screenshot:', error);
            alert('Failed to take screenshot. Please try again.');
        }
    };

    const handleSendEmail = async () => {
        if (!screenshotUrl) return;

        setIsLoading(true);
        try {
            // Check if user is authenticated
            if (!user) {
                throw new Error('User must be logged in to send screenshots');
            }

            // Force token refresh to ensure we have a valid token
            await user.getIdToken(true);

            const screenshotBlob = await fetch(screenshotUrl).then(r => r.blob());
            const screenshotRef = ref(storage, `screenshots/${Date.now()}.png`);
            await uploadBytes(screenshotRef, screenshotBlob);
            const downloadUrl = await getDownloadURL(screenshotRef);

            const userAgent = navigator.userAgent;
            const deviceInfo = {
                userAgent,
                platform: (navigator as any).userAgentData?.platform || 'Unknown',
                language: navigator.language,
                screenWidth: window.screen.width,
                screenHeight: window.screen.height,
                windowWidth: window.innerWidth,
                windowHeight: window.innerHeight,
                devicePixelRatio: window.devicePixelRatio,
                timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
                dateTime: new Date().toISOString()
            };

            const sendEmail = httpsCallable(functions, 'sendScreenshotEmail');
            await sendEmail({
                screenshotUrl: downloadUrl,
                userEmail: user.email || 'user@example.com',
                context: context,
                deviceInfo: JSON.stringify(deviceInfo),
                userName: user.displayName || 'Unknown User'
            });

            setShowModal(false);
            setContext('');
            setScreenshotUrl(null);
            setShowToast(true);
        } catch (error) {
            console.error('Error sending screenshot:', error);

            // Provide more specific error information
            let errorMessage = 'Failed to send screenshot. Please try again.';

            if (error && typeof error === 'object' && 'code' in error) {
                switch (error.code) {
                    case 'functions/unavailable':
                        errorMessage = 'Screenshot service is currently unavailable. Please try again later.';
                        break;
                    case 'functions/unauthenticated':
                        errorMessage = 'Your login session has expired. Please log in again to send screenshots.';
                        break;
                    case 'functions/permission-denied':
                        errorMessage = 'You do not have permission to send screenshots.';
                        break;
                    case 'functions/internal':
                        errorMessage = 'Internal server error. Please try again later or contact support.';
                        break;
                    default:
                        errorMessage = `Error: ${error.code || 'Unknown error'}. Please try again.`;
                }
            } else if (error instanceof Error) {
                if (error.message.includes('User must be logged in')) {
                    errorMessage = 'Please log in to send screenshots.';
                } else {
                    errorMessage = error.message;
                }
            }

            alert(errorMessage);
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div>
            <button
                onClick={handleScreenshot}
                disabled={isLoading}
                style={buttonStyle}
                title="Take Screenshot"
            >
                {isLoading ? 'Processing...' : <ReportIcon color={theme.colors.WHITE} />}
            </button>

            <Modal
                isOpen={showModal}
                onClose={() => setShowModal(false)}
                onConfirm={handleSendEmail}
                title="Report Issue / Provide Feedback"
                message="A screenshot was just captured. Please Enter some context for the issue or feedback:"
                confirmText="Send Report"
                cancelText="Cancel"
                disabled={isLoading}
                showTextField
                textFieldValue={context}
                onTextFieldChange={setContext}
            />

            <Toast
                message="Issue reported successfully"
                isVisible={showToast}
                onClose={() => setShowToast(false)}
            />
        </div>
    );
};

export default ScreenshotButton; 